import React, {useEffect} from 'react'
import {useForm} from 'react-hook-form'
import {Stack, TextField, Typography} from "@mui/material";
import {styled} from "@mui/material/styles";
import {FormIds} from "../../constants";
import InputAdornment from "@mui/material/InputAdornment";
import {useTranslation} from "react-i18next";

const SectionHeaderContainer = styled("div")({
  width: "100%",
  textAlign: "left",
  paddingTop: "2rem",
  paddingBottom: ".5rem"
})

export interface BillingConfigFormValues {
  semdatexFixedCosts: string
  getemedFixedCosts: string
  getemedHotlinePrice: string
  semdatexImplantRevenueShare: string
  biotronikImplantRevenueShare: string
  DocCirrusImplantRevenueShare: string
  semdatexExternalSensorsRevenueShare: string
  getemedExternalSensorsRevenueShare: string
  biotronikExternalSensorsRevenueShare: string
  DocCirrusExternalSensorsRevenueShare: string
  sapMaterialExtSensors: string
  sapMaterialExtSensorsGetemed: string
  sapMaterialImplantsGba: string
  sapMaterialImplantsNonGba: string
}

const SAP_MATERIAL_FIELDS = [
  "sapMaterialExtSensors",
  "sapMaterialExtSensorsGetemed",
  "sapMaterialImplantsGba",
  "sapMaterialImplantsNonGba",
] as const

interface BillingConfigFormProps {
  onSubmit: (data: BillingConfigFormValues) => void
  /** This function updates the dirty state in the parents component */
  updateDirty: (dirty: boolean) => void
  defaultValues: BillingConfigFormValues
}

export default function BillingConfigForm({onSubmit, defaultValues, updateDirty}: Readonly<BillingConfigFormProps>) {

  const form = useForm<BillingConfigFormValues>({
    defaultValues
  })

  const {t} = useTranslation()

  const {register, handleSubmit, formState} = form

  const {errors} = formState

  useEffect(() => {
    updateDirty(form.formState.isDirty)
  }, [form.formState.isDirty])

  return (
    <form id={FormIds.billingConfig} onSubmit={handleSubmit(onSubmit)} noValidate>
      <Stack spacing={3}>
        <Stack spacing={2}>
          <SectionHeaderContainer>
            <Typography variant="h5">{t('form.fields.configuration.fixedCostsHeader')}</Typography>
          </SectionHeaderContainer>
          <Stack direction="row"
                 spacing={5}
                 sx={{justifyItems: 'stretch'}}
          >
            <TextField
              sx={{width: '17rem'}}
              id={"semdatexFixedCosts"}
              variant="outlined"
              label={t('form.fields.configuration.sdxFixedCosts')}
              type="number"
              required
              {...register("semdatexFixedCosts", {
                required: t('form.fields.configuration.sdxFixedCostsRequired')
              })}
              error={!!errors.semdatexFixedCosts}
              helperText={errors.semdatexFixedCosts?.message}
              slotProps={{input: {endAdornment: <InputAdornment position={'end'}>€</InputAdornment>}}}
            />
            <TextField
              sx={{width: '17rem'}}
              id={"getemedFixedCosts"}
              variant="outlined"
              label={t('form.fields.configuration.getemedFixedCosts')}
              type="number"
              required
              {...register("getemedFixedCosts", {
                required: t('form.fields.configuration.getemedFixedCostsRequired')
              })}
              error={!!errors.getemedFixedCosts}
              helperText={errors.getemedFixedCosts?.message}
              slotProps={{input: {endAdornment: <InputAdornment position={'end'}>€</InputAdornment>}}}
            />
            <TextField
              sx={{width: '17rem'}}
              id={"getemedHotlinePrice"}
              variant="outlined"
              label={t('form.fields.configuration.getemedHotlinePrice')}
              type="number"
              required
              {...register("getemedHotlinePrice", {
                required: t('form.fields.configuration.getemedHotlinePriceRequired')
              })}
              error={!!errors.getemedHotlinePrice}
              helperText={errors.getemedHotlinePrice?.message}
              slotProps={{input: {endAdornment: <InputAdornment position={'end'}>€</InputAdornment>}}}
            />
          </Stack>
        </Stack>
        <Stack spacing={2}>
          <SectionHeaderContainer>
            <Typography variant="h5">{t('pages.configurationPage.externalSensorsSharesTitle')}</Typography>
          </SectionHeaderContainer>
          <Stack
            direction="row"
            spacing={5}
          >
            <TextField
              id={"semdatexExternalSensorsRevenueShare"}
              variant="outlined"
              label={t("form.fields.configuration.semdatexExternalSensorsRevenueShare")}
              type="number"
              required
              {...register("semdatexExternalSensorsRevenueShare", {
                required: t("form.fields.configuration.semdatexExternalSensorsRevenueShareRequired")
              })}
              error={!!errors.semdatexExternalSensorsRevenueShare}
              helperText={errors.semdatexExternalSensorsRevenueShare?.message}
              slotProps={{input: {endAdornment: <InputAdornment position={'end'}>%</InputAdornment>}}}
            />
            <TextField
              id={"getemedExternalSensorsRevenueShare"}
              variant="outlined"
              label={t("form.fields.configuration.getemedShare")}
              type="number"
              required
              {...register("getemedExternalSensorsRevenueShare", {
                required: t("form.fields.configuration.getemedShareRequired")
              })}
              error={!!errors.getemedExternalSensorsRevenueShare}
              helperText={errors.getemedExternalSensorsRevenueShare?.message}
              slotProps={{input: {endAdornment: <InputAdornment position={'end'}>%</InputAdornment>}}}
            />
            <TextField
              id={"biotronikExternalSensorsRevenueShare"}
              variant="outlined"
              label={t("form.fields.configuration.biotronikShare")}
              type="number"
              required
              {...register("biotronikExternalSensorsRevenueShare", {
                required: t("form.fields.configuration.biotronikShareRequired")
              })}
              error={!!errors.biotronikExternalSensorsRevenueShare}
              helperText={errors.biotronikExternalSensorsRevenueShare?.message}
              slotProps={{input: {endAdornment: <InputAdornment position={'end'}>%</InputAdornment>}}}
            />
            <TextField
              id={"DocCirrusExternalSensorsRevenueShare"}
              variant="outlined"
              label={t("form.fields.configuration.docCirrusShare")}
              type="number"
              required
              {...register("DocCirrusExternalSensorsRevenueShare", {
                required: t("form.fields.configuration.docCirrusShareRequired")
              })}
              error={!!errors.DocCirrusExternalSensorsRevenueShare}
              helperText={errors.DocCirrusExternalSensorsRevenueShare?.message}
              slotProps={{input: {endAdornment: <InputAdornment position={'end'}>%</InputAdornment>}}}
            />
          </Stack>
        </Stack>

        <Stack spacing={2}>
          <SectionHeaderContainer>
            <Typography variant="h5">{t('pages.configurationPage.implantSharesTitle')}</Typography>
          </SectionHeaderContainer>
          <Stack
            direction="row"
            spacing={5}
          >
            <TextField
              id={"semdatexImplantRevenueShare"}
              variant="outlined"
              label={t("form.fields.configuration.semdatexImplantShare")}
              type="number"
              required
              {...register("semdatexImplantRevenueShare", {
                required: t("form.fields.configuration.semdatexImplantShareRequired")
              })}
              error={!!errors.semdatexImplantRevenueShare}
              helperText={errors.semdatexImplantRevenueShare?.message}
              slotProps={{input: {endAdornment: <InputAdornment position={'end'}>%</InputAdornment>}}}
            />
            <TextField
              id={"biotronikImplantRevenueShare"}
              variant="outlined"
              label={t("form.fields.configuration.biotronikImplantShare")}
              type="number"
              required
              {...register("biotronikImplantRevenueShare", {
                required: t("form.fields.configuration.biotronikImplantShareRequired")
              })}
              error={!!errors.biotronikImplantRevenueShare}
              helperText={errors.biotronikImplantRevenueShare?.message}
              slotProps={{input: {endAdornment: <InputAdornment position={'end'}>%</InputAdornment>}}}
            />
            <TextField
              id={"DocCirrusImplantRevenueShare"}
              variant="outlined"
              label={t('form.fields.configuration.docCirrusImplantShare')}
              type="number"
              required
              {...register("DocCirrusImplantRevenueShare", {
                required: t('form.fields.configuration.docCirrusImplantShareRequired')
              })}
              error={!!errors.DocCirrusImplantRevenueShare}
              helperText={errors.DocCirrusImplantRevenueShare?.message}
              slotProps={{input: {endAdornment: <InputAdornment position={'end'}>%</InputAdornment>}}}
            />
          </Stack>
        </Stack>

        <Stack spacing={2}>
          <SectionHeaderContainer>
            <Typography variant="h5">{t('pages.configurationPage.sapMaterialNumbersTitle')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t('pages.configurationPage.sapMaterialNumbersDescription')}
            </Typography>
          </SectionHeaderContainer>
          <Stack
            direction="row"
            spacing={5}
          >
            {SAP_MATERIAL_FIELDS.map(field => (
              <TextField
                key={field}
                id={field}
                variant="outlined"
                label={t(`form.fields.configuration.${field}`)}
                {...register(field)}
              />
            ))}
          </Stack>
        </Stack>
      </Stack>
    </form>
  )
}
